import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { SearchTypes } from '../../constants/CentricConstants';
import { browserHistory } from 'react-router';

import ListView from '../Common/ListView.react';
import ListItem from './UserListItem';
import ConfirmDialog from '../Common/ConfirmDialog';
import RestoreErrorDialog from './RestoreErrorDialog';
import ErrorSnackbar from '../Common/ErrorSnackbar';
import FlatButton from 'material-ui/FlatButton';
import LinearProgress from 'material-ui/LinearProgress';

import DialogActions from '../../actions/DialogActionCreator';
import UserActions from '../../actions/UserActionCreator';
import UserStore from '../../stores/UserStore';

import MailIcon from 'material-ui/svg-icons/content/send';
import FileUploadIcon from 'material-ui/svg-icons/communication/import-export';
import CancelIcon from 'material-ui/svg-icons/navigation/cancel';
import UpIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-up';
import DownIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-down';
import DownloadIcon from 'material-ui/svg-icons/file/file-download';
import SyncIcon from 'material-ui/svg-icons/notification/sync'

import { CommonDialogs } from '../../constants/Dialogs';
import ROLES from '../../constants/Roles';
import buildContextMenu from '../Common/BuildContextMenu';

class UserList extends Component {

    constructor() {
        super(...arguments);
        this.state = {
            users: [],
            count: 0,
            page: 0,
            limit: 50,
            searchPage: 0,
            searchMode: !!this.props.location.query.searchTerm,
            searchText: null,
            loading: false,
            fetchedAll: false,
            showButton: false,
            showDeleted: !!this.context.settings.get('SHOW_DELETED_USERS'),
            confirmDialogProps: null
        };
    }

    componentDidMount() {
        this.onChange = this.onChange.bind(this);
        this.sort = this.sort.bind(this);
        this.loadNextPage = this.loadNextPage.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.throttleSearch = this.throttleSearch.bind(this);
        this.toggleSearchMode = this.toggleSearchMode.bind(this);
        this.onRestoreDeleted = this.onRestoreDeleted.bind(this);
        this.onRestoreArchived = this.onRestoreArchived.bind(this);

        UserStore.addChangeListener(this.onChange);
        UserActions.getAll({limit: this.state.limit, includeDeleted: this.state.showDeleted, includeArchived: true});

        if (!!this.props.location.query.searchTerm) {
            this.throttleSearch(this.props.location.query.searchTerm);
        }

        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount() {
        UserStore.removeChangeListener(this.onChange);
        window.removeEventListener('scroll', this.handleScroll);
    }

    render() {
        const isAdmin = this.context.auth.isAdmin();
        const newEntityUrl = isAdmin ? '/dashboard/user/new' : '';

        const getMenuItems = () => [
            {
                action: this.onSendWelcomeEmails,
                icon: <MailIcon />,
                label: 'Send welcome email(s)',
                condition: {
                    roles: [ROLES.ADMIN]
                }
            },
            {
                icon: <FileUploadIcon />,
                link: '/dashboard/users/import',
                label: 'Import users (csv)',
                condition: {
                    roles: [ROLES.ADMIN]
                }
            },
            {
                icon: <DownloadIcon />,
                link: '/usr/users/export.csv',
                target: '_blank',
                label: 'Export users (csv)',
                condition: {
                    roles: [ROLES.ADMIN]
                }
            },
            {
                type: 'divider'
            },
            {
                action: this.onSync,
                icon: <SyncIcon />,
                label: 'Sync with IAM',
                condition: {
                    roles: [ROLES.ADMIN]
                }
            },
            {
                type: 'divider'
            },
            {
                link: '/logout',
                icon: <CancelIcon />,
                label: 'Sign out'
            }
        ];

        const actions = buildContextMenu(getMenuItems(), this.context.auth);

        return (
            <div ref="userList">
                <ListView
                    title="Users"
                    sortOptions={this.props.sortOptions}
                    sortAction={this.sort}
                    searchAction={this.throttleSearch}
                    searchMode={this.state.searchMode}
                    searchType={SearchTypes.USERS}
                    items={this.state.users}
                    itemDecorator={ListItem}
                    itemProps={{onRestoreDeleted: this.onRestoreDeleted, onRestoreArchived: this.onRestoreArchived}}
                    newEntityUrl={newEntityUrl}
                    actions={actions}
                    toggleSearchMode={this.toggleSearchMode}
                />
                {this.state.loading && (
                    <LinearProgress mode="indeterminate" />
                )}
                {this.state.showButton && (
                    <FlatButton
                        label={this.state.fetchedAll ? 'Back to top' : 'Load more'}
                        icon={this.state.fetchedAll ? <UpIcon/> : <DownIcon/>}
                        onTouchTap={this.state.fetchedAll ? () => window.scrollTo(0, 0) : this.loadNextPage}
                        disabled={this.state.loading}
                        fullWidth
                    />
                )}
                <ErrorSnackbar/>
                <RestoreErrorDialog/>
                {!!this.state.confirmDialogProps && (
                    <ConfirmDialog {...this.state.confirmDialogProps} />
                )}
            </div>
        );
    }

    onChange() {
        const users = UserStore.getAll();
        if (!!users) {
            if (users.length <= 1 || !this.state.sortCriteria || !this.state.sortMapCriteria) {
                this.setState({users, loading: false});
            } else {
                const mapped = users.map(this.state.sortMapCriteria);
                mapped.sort(this.state.sortCriteria);
                const sortedAssignments = mapped.map((el) => users[el.index]);
                this.setState({users: sortedAssignments, loading: false});
            }
            if (UserStore.fetchedAll()) {
                this.setState({fetchedAll: true});
            }
        }
    }

    onSendWelcomeEmails() {
        if (confirm("Send welcome emails ?")) {
            UserActions.sendMissingWelcomeEmails();
        }
    }

    onSync() {
        if (confirm("Synchronise users with IAM?")) {
            UserActions.syncWithIAM();
        }
    }

    onRestoreDeleted(id, name) {
        this.setState({
            confirmDialogProps: {
                title: 'Undo delete',
                confirmButtonLabel: 'Undo',
                message: `This action will restore ${name}`,
                onConfirm: () => UserActions.restoreDeletedUser(id)
            }
        });
        DialogActions.open(CommonDialogs.CONFIRM);
    }

    onRestoreArchived(id, name) {
        this.setState({
            confirmDialogProps: {
                title: 'Restore archived user',
                confirmButtonLabel: 'Restore',
                message: `This action will restore ${name}`,
                onConfirm: () => UserActions.restoreArchivedUser(id)
            }
        });
        DialogActions.open(CommonDialogs.CONFIRM);
    }

    throttleSearch(searchText) {
        this.timeout && clearTimeout(this.timeout);
        if (!!searchText && searchText.length > 2) {
            this.timeout = setTimeout(() => { UserActions.search(searchText, {limit: this.state.limit, includeDeleted: this.state.showDeleted, includeArchived: true}); }, 500);

            if (searchText !== this.state.searchText) {
                this.setState({fetchedAll: false, showButton: false});
            }
            this.setState({searchText, searchPage: 0});
            browserHistory.replace(`${this.props.location.pathname}?searchTerm=${searchText}`);
        }
    }

    sort(options) {
        let sortCriteria, sortMapCriteria;
        switch (options.sort) {
            case 'firstName':
                sortCriteria = (a, b) => a.value.localeCompare(b.value);
                sortMapCriteria = (el, i) => ({ index: i, value: el.firstName });
                break;
            case 'lastName':
                sortCriteria = (a, b) => a.value.localeCompare(b.value);
                sortMapCriteria = (el, i) => ({ index: i, value: el.lastName });
                break;
            case 'jobRole':
                sortCriteria = (a, b) => a.value.localeCompare(b.value);
                sortMapCriteria = (el, i) => ({ index: i, value: el.jobRole });
                break;
            case 'visits':
                sortCriteria = (a, b) => (b.value - a.value);
                sortMapCriteria = (el, i) => ({ index: i, value: el.visits });
                break;
            case 'dateAdded':
                sortCriteria = (a, b) => (b.value - a.value);
                sortMapCriteria = (el, i) => ({index: i, value: Date.parse(el.dateAdded)});
                break;
            default:
                sortCriteria = (a, b) => (a.value - b.value);
                sortMapCriteria = (el, i) => ({ index: i, value: el[this.state.sort] });
        }
        this.setState({ sort: options.sort, sortCriteria, sortMapCriteria });
        UserActions.getAll({sort: options.sort});
    }

    loadNextPage() {
        if (this.state.loading) { return; } //guard

        const page = !this.state.searchMode ? (this.state.page + 1) : (this.state.searchPage + 1);
        if (!this.state.searchMode) {
            UserActions.getAll({ page, limit: this.state.limit, includeDeleted: this.state.showDeleted, includeArchived: true });
            this.setState({page, loading: true});
        } else if (!!this.state.searchText) {
            UserActions.search(this.state.searchText, {page, limit: this.state.limit, includeDeleted: this.state.showDeleted, includeArchived: true});
            this.setState({searchPage: page, loading: true});
        }
    }

    handleScroll() {
        if (!this.state.showButton && window.scrollY > 0) {
            this.setState({showButton: true});
        }
        if (!this.state.fetchedAll && (window.scrollY + window.innerHeight) >= this.refs.userList.clientHeight) {
            this.loadNextPage();
        }
    }

    toggleSearchMode(searchMode) {
        if (!searchMode) {
            this.setState({searchText: null});
            browserHistory.replace(this.props.location.pathname);
        }
        this.setState({searchMode, fetchedAll: false, showButton: false});
    }
}

UserList.contextTypes = {
    auth: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired
};

UserList.defaultProps = {
    sortOptions: [
        {label: "First name", value: 'firstName'},
        {label: "Last name", value: 'lastName'},
        {label: "Job role", value: 'jobRole'},
        {label: "Visits", value: 'visits'},
        {label: "Date created", value: 'dateAdded'}
    ]
};

export default UserList;
